/**
 * RoadClosureLayer Component
 *
 * Displays NSW and VIC road closures and traffic hazards on the map.
 * Data is fetched from a static GeoJSON file served via CloudFront,
 * sourced from TfNSW Live Traffic API (NSW) and VicRoads/DTP (VIC).
 *
 * IMPORTANT: Road closures help users avoid blocked roads during emergencies.
 * Always check current conditions before travelling.
 */

import { useEffect, useState, useMemo, Fragment } from 'react';
import { Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import { fetchJsonInWorker } from '../workers/fetchJsonInWorker';

/**
 * Create an SVG warning triangle icon for road hazards.
 * The triangle shape instantly distinguishes these from circular hotspot markers.
 */
function createHazardIcon(color: string, isClosure: boolean): L.DivIcon {
  const size = isClosure ? 28 : 24;
  const halfSize = size / 2;

  // SVG warning triangle with exclamation mark
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="${size}" height="${size}">
      <path d="M12 2L1 21h22L12 2z" fill="${color}" stroke="#fff" stroke-width="1.5"/>
      <path d="M12 9v5" stroke="#fff" stroke-width="2.5" stroke-linecap="round"/>
      <circle cx="12" cy="17" r="1.5" fill="#fff"/>
    </svg>
  `;

  return L.divIcon({
    html: svg,
    className: 'road-hazard-icon',
    iconSize: [size, size],
    iconAnchor: [halfSize, size], // Anchor at bottom center of triangle
    popupAnchor: [0, -size + 4], // Popup appears above the icon
  });
}

// Road closures GeoJSON URL - served from CloudFront
const ROAD_CLOSURES_URL = import.meta.env.VITE_ROAD_CLOSURES_URL || '';

// Refresh interval - matches ingestion (5 minutes)
const REFRESH_INTERVAL_MS = 5 * 60 * 1000;

interface EncodedPolyline {
  coords: string;
  direction?: string;
  levels?: string;
}

interface RoadClosureFeature {
  type: 'Feature';
  geometry: {
    type: 'Point';
    coordinates: [number, number]; // [lon, lat]
  };
  properties: {
    id: string;
    headline: string;
    adviceA: string;
    adviceB: string;
    isMajor: boolean;
    isClosure: boolean;
    category: string;
    subCategory: string;
    roads: string[];
    created: number | string; // Unix timestamp or ISO string
    lastUpdated: number | string; // Unix timestamp or ISO string
    endTime: number | string; // Unix timestamp or ISO string
    nextUpdateDue?: number | string; // VIC only: when the next update is expected
    encodedPolylines: EncodedPolyline[];  // NSW uses Google-encoded polylines
    rawCoordinates?: [number, number][];  // VIC uses raw GeoJSON coordinates [[lon,lat],...]
    hazardType: string;
    state?: 'NSW' | 'VIC'; // State source of the closure
  };
}

interface RoadClosureGeoJSON {
  type: 'FeatureCollection';
  features: RoadClosureFeature[];
  metadata: {
    source: string;
    generatedAt: string;
    count: number;
    disclaimer: string;
  };
}

// No props needed - component is conditionally rendered by parent

/**
 * Categories to show - events that could actually block roads during emergencies.
 * Excludes routine scheduled roadwork (pothole fixes, line marking, etc.)
 */
const IMPORTANT_CATEGORIES = new Set([
  'HAZARD',
  'CRASH',
  'BREAKDOWN',
  'ADVERSE WEATHER',
  'FERRY OUT OF SERVICE',
  'CHANGED TRAFFIC CONDITIONS',
  'INCIDENT',
  'FLOOD',
  'BUSHFIRE',
]);

/**
 * Check if a road event is important enough to display.
 * We want events that could block escape routes during fires,
 * not routine scheduled roadwork.
 */
function isImportantEvent(props: RoadClosureFeature['properties']): boolean {
  // Always show closures and major events
  if (props.isClosure) return true;
  if (props.isMajor) return true;

  // Show fire-related hazards
  if (props.hazardType === 'fire') return true;

  // Filter out roadwork early - these don't block roads during emergencies
  const category = props.category?.toUpperCase() || '';
  const hazardType = props.hazardType?.toLowerCase() || '';
  if (category === 'SCHEDULED ROADWORK' ||
      category === 'ROADWORK' ||
      hazardType === 'roadwork') {
    return false;
  }

  // Show if category is in our important list
  if (IMPORTANT_CATEGORIES.has(category)) return true;

  // Check subCategory for important keywords
  const subCategory = props.subCategory?.toLowerCase() || '';
  if (subCategory.includes('closure') ||
      subCategory.includes('flood') ||
      subCategory.includes('fire') ||
      subCategory.includes('accident') ||
      subCategory.includes('crash')) {
    return true;
  }

  // Default: hide unknown events (be strict to reduce clutter)
  return false;
}

/**
 * Get color based on hazard type and severity.
 */
function getHazardColor(props: RoadClosureFeature['properties']): string {
  if (props.isClosure) return '#dc2626'; // Red for closures
  if (props.isMajor) return '#f97316'; // Orange for major hazards
  if (props.hazardType === 'fire') return '#ef4444'; // Red-ish for fire hazards
  if (props.hazardType === 'roadwork') return '#eab308'; // Yellow for roadworks
  return '#6b7280'; // Gray for other hazards
}

/**
 * Decode Google encoded polyline to array of [lat, lon] coordinates.
 * Based on Google's polyline algorithm.
 */
function decodePolyline(encoded: string): [number, number][] {
  const points: [number, number][] = [];
  let index = 0;
  let lat = 0;
  let lng = 0;

  while (index < encoded.length) {
    let shift = 0;
    let result = 0;
    let byte: number;

    do {
      byte = encoded.charCodeAt(index++) - 63;
      result |= (byte & 0x1f) << shift;
      shift += 5;
    } while (byte >= 0x20);

    const dlat = result & 1 ? ~(result >> 1) : result >> 1;
    lat += dlat;

    shift = 0;
    result = 0;

    do {
      byte = encoded.charCodeAt(index++) - 63;
      result |= (byte & 0x1f) << shift;
      shift += 5;
    } while (byte >= 0x20);

    const dlng = result & 1 ? ~(result >> 1) : result >> 1;
    lng += dlng;

    points.push([lat / 1e5, lng / 1e5]);
  }

  return points;
}

/**
 * Format relative time for display.
 * Handles both Unix timestamps (milliseconds) and ISO strings.
 */
function formatTimeAgo(timestamp: number | string): string {
  if (!timestamp) return 'Unknown';

  // Handle Unix timestamp (milliseconds) or ISO string
  const date = typeof timestamp === 'number'
    ? new Date(timestamp)
    : new Date(timestamp);

  if (isNaN(date.getTime())) return 'Unknown';

  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 0) return 'Just now'; // Future dates
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} min ago`;

  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;

  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 30) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;

  // For older dates, show the actual date
  return date.toLocaleDateString();
}

/**
 * Format a timestamp for display as a date.
 */
function formatDate(timestamp: number | string): string {
  if (!timestamp) return 'Unknown';

  const date = typeof timestamp === 'number'
    ? new Date(timestamp)
    : new Date(timestamp);

  if (isNaN(date.getTime())) return 'Unknown';

  return date.toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
}

/**
 * RoadClosureLayer - displays traffic hazards and road closures on the map.
 */
export default function RoadClosureLayer() {
  const map = useMap();
  const [closures, setClosures] = useState<RoadClosureGeoJSON | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Cleanup orphaned DivIcon markers on unmount
  // React-Leaflet's Marker with DivIcon can leave DOM elements behind
  useEffect(() => {
    return () => {
      // Remove any orphaned road hazard icons from the map container
      const mapContainer = map.getContainer();
      const orphanedIcons = mapContainer.querySelectorAll('.road-hazard-icon');
      orphanedIcons.forEach(icon => {
        // Remove the parent marker element (the leaflet-marker-icon wrapper)
        const markerWrapper = icon.closest('.leaflet-marker-icon');
        if (markerWrapper) {
          markerWrapper.remove();
        } else {
          icon.remove();
        }
      });
    };
  }, [map]);

  // Fetch road closures GeoJSON
  useEffect(() => {
    if (!ROAD_CLOSURES_URL) {
      return;
    }

    const fetchClosures = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // Fetch + parse off the main thread — the ~1.3MB parse otherwise
        // blocked the main thread on load and every refresh.
        const data = await fetchJsonInWorker<RoadClosureGeoJSON>(ROAD_CLOSURES_URL, {
          Accept: 'application/geo+json, application/json',
        });
        setClosures(data);
        const importantCount = data.features.filter(f => isImportantEvent(f.properties)).length;
        console.log(`[RoadClosureLayer] Loaded ${data.features.length} road events, ${importantCount} important (filtered out ${data.features.length - importantCount} routine roadwork)`);
      } catch (err) {
        console.error('[RoadClosureLayer] Error fetching road closures:', err);
        setError(err instanceof Error ? err.message : 'Failed to load road closures');
      } finally {
        setIsLoading(false);
      }
    };

    // Initial fetch
    fetchClosures();

    // Refresh — skip while the tab is hidden (avoids the ~1.3MB download +
    // parse in the background); refetch once on becoming visible again.
    const interval = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return;
      fetchClosures();
    }, REFRESH_INTERVAL_MS);
    const onVisible = () => {
      if (typeof document !== 'undefined' && !document.hidden) fetchClosures();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  // Get current viewport bounds for filtering
  const [viewBounds, setViewBounds] = useState<L.LatLngBounds | null>(null);

  useEffect(() => {
    // 150ms debounce — flick-pans emit several moveend events as inertia
    // settles; without this we re-filter every road-closure polygon on
    // each one. P0-7 in the 2D perf audit.
    let timer: ReturnType<typeof setTimeout> | null = null;
    const updateBounds = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        setViewBounds(map.getBounds());
      }, 150);
    };
    updateBounds();
    map.on('moveend', updateBounds);
    map.on('zoomend', updateBounds);
    return () => {
      if (timer) clearTimeout(timer);
      map.off('moveend', updateBounds);
      map.off('zoomend', updateBounds);
    };
  }, [map]);

  // Filter closures to viewport with buffer, and only show important events
  // Also group VIC points by road to create synthetic polylines
  const { visibleClosures, vicRoadPolylines } = useMemo(() => {
    if (!closures || !viewBounds) return { visibleClosures: [], vicRoadPolylines: [] };

    // Add 20% buffer for smooth panning
    const latSpan = viewBounds.getNorth() - viewBounds.getSouth();
    const lngSpan = viewBounds.getEast() - viewBounds.getWest();
    const latBuffer = latSpan * 0.2;
    const lngBuffer = lngSpan * 0.2;

    const bufferedBounds = L.latLngBounds(
      [viewBounds.getSouth() - latBuffer, viewBounds.getWest() - lngBuffer],
      [viewBounds.getNorth() + latBuffer, viewBounds.getEast() + lngBuffer]
    );

    const filtered = closures.features.filter(feature => {
      // First filter: only show important events (not routine roadwork)
      if (!isImportantEvent(feature.properties)) return false;

      // Second filter: within viewport bounds
      const [lon, lat] = feature.geometry.coordinates;
      return bufferedBounds.contains([lat, lon]);
    });

    // Group VIC closures by road name to create polylines connecting points
    // VIC API returns individual Point geometries, not LineStrings like NSW
    const vicByRoad = new Map<string, RoadClosureFeature[]>();
    const nonVicFeatures: RoadClosureFeature[] = [];

    for (const feature of filtered) {
      if (feature.properties.state === 'VIC') {
        const roadName = feature.properties.roads?.[0] || 'Unknown';
        const existing = vicByRoad.get(roadName) || [];
        existing.push(feature);
        vicByRoad.set(roadName, existing);
      } else {
        nonVicFeatures.push(feature);
      }
    }

    // Create polylines for VIC roads with 2+ points
    // Sort points by latitude to create a reasonable line along the road
    const polylines: { key: string; positions: [number, number][]; color: string; isClosure: boolean }[] = [];
    const vicRepresentatives: RoadClosureFeature[] = []; // One marker per road

    for (const [roadName, features] of vicByRoad.entries()) {
      if (features.length >= 2) {
        // Sort by latitude (generally roads run N-S or we can approximate)
        const sorted = [...features].sort((a, b) => {
          const [lonA, latA] = a.geometry.coordinates;
          const [lonB, latB] = b.geometry.coordinates;
          // Sort by latitude first, then longitude
          if (Math.abs(latA - latB) > 0.001) return latA - latB;
          return lonA - lonB;
        });

        // Create polyline positions
        const positions: [number, number][] = sorted.map(f => {
          const [lon, lat] = f.geometry.coordinates;
          return [lat, lon];
        });

        // Use color from first (most severe) feature
        const color = getHazardColor(features[0].properties);
        const isClosure = features.some(f => f.properties.isClosure);

        polylines.push({
          key: `vic-road-${roadName}`,
          positions,
          color,
          isClosure
        });

        // Add only the first feature as representative marker (middle of sorted list)
        const midIdx = Math.floor(sorted.length / 2);
        vicRepresentatives.push(sorted[midIdx]);
      } else {
        // Single point - show as normal marker
        vicRepresentatives.push(features[0]);
      }
    }

    return {
      visibleClosures: [...nonVicFeatures, ...vicRepresentatives],
      vicRoadPolylines: polylines
    };
  }, [closures, viewBounds]);

  // Precompute decoded polylines per feature ONCE (keyed on the visible set),
  // so the expensive Google-polyline decode doesn't re-run in the render body on
  // every pan. Same geometry, just cached instead of recomputed.
  const polylinesByFeature = useMemo(() => {
    const byId = new Map<string, { key: string; positions: [number, number][] }[]>();
    for (const feature of visibleClosures) {
      const props = feature.properties;
      const polylines: { key: string; positions: [number, number][] }[] = [];
      if (props.encodedPolylines && props.encodedPolylines.length > 0) {
        props.encodedPolylines.forEach((polylineObj, idx) => {
          try {
            const encoded = typeof polylineObj === 'string' ? polylineObj : polylineObj?.coords;
            if (encoded) {
              const positions = decodePolyline(encoded);
              if (positions.length >= 2) polylines.push({ key: `${props.id}-poly-${idx}`, positions });
            }
          } catch { /* skip invalid polyline */ }
        });
      }
      if (props.rawCoordinates && props.rawCoordinates.length >= 2) {
        try {
          const positions: [number, number][] = props.rawCoordinates.map(
            ([coordLon, coordLat]) => [coordLat, coordLon]
          );
          if (positions.length >= 2) polylines.push({ key: `${props.id}-raw`, positions });
        } catch { /* skip invalid coordinates */ }
      }
      byId.set(props.id, polylines);
    }
    return byId;
  }, [visibleClosures]);

  if (!ROAD_CLOSURES_URL) {
    return null;
  }

  if (isLoading && !closures) {
    return null; // Don't show loading indicator on map
  }

  if (error) {
    console.warn('[RoadClosureLayer] Error state:', error);
    return null; // Fail silently on map
  }

  // Render road closure markers and polylines
  return (
    <>
      {/* VIC road polylines - connecting multiple points on the same road */}
      {vicRoadPolylines.map(({ key, positions, color, isClosure }) => (
        <Polyline
          key={key}
          positions={positions}
          pathOptions={{
            color: color,
            weight: 5,
            opacity: 0.85,
            dashArray: isClosure ? undefined : '12, 8',
          }}
        />
      ))}

      {visibleClosures.map((feature) => {
        const [lon, lat] = feature.geometry.coordinates;
        const props = feature.properties;
        const color = getHazardColor(props);

        // Precomputed above (decoded once per visible set), not per render.
        const polylines = polylinesByFeature.get(props.id) || [];

        return (
          <Fragment key={props.id}>
            {/* Render polylines for affected road segments */}
            {polylines.map(({ key, positions }) => (
              <Polyline
                key={key}
                positions={positions}
                pathOptions={{
                  color: color,
                  weight: 5,
                  opacity: 0.85,
                  dashArray: props.isClosure ? undefined : '12, 8',
                }}
              />
            ))}

            {/* Marker at hazard location - warning triangle icon */}
            <Marker
              position={[lat, lon]}
              icon={createHazardIcon(color, props.isClosure)}
            >
              {/* autoPan disabled: the default jerks the whole map to re-centre
                  the popup the instant a user taps a closure — disorienting
                  mid-evacuation. Keep the map still; the popup renders in place.
                  uplift plan §3.3. */}
              <Popup maxWidth={420} minWidth={360} autoPan={false} className="road-closure-popup-wrapper">
                <div className="road-closure-popup">
                  {/* Header - colored banner */}
                  <div style={{
                    backgroundColor: color,
                    color: 'white',
                    padding: '12px 16px',
                  }}>
                    <div style={{
                      fontSize: '10px',
                      opacity: 0.9,
                      marginBottom: '4px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      fontWeight: '500'
                    }}>
                      {props.isClosure ? 'Road Closure' : props.hazardType === 'roadwork' ? 'Roadwork' : 'Traffic Hazard'}
                    </div>
                    <div style={{ fontWeight: '600', fontSize: '15px', lineHeight: '1.3' }}>
                      {props.headline || props.category || 'Traffic Alert'}
                    </div>
                  </div>

                  {/* Body content */}
                  <div style={{ padding: '14px 16px', backgroundColor: 'var(--bg-elevated)' }}>
                    {/* Status badges */}
                    <div style={{ marginBottom: '12px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {props.isClosure && (
                        <span style={{
                          backgroundColor: '#dc2626',
                          color: 'white',
                          padding: '4px 12px',
                          borderRadius: '14px',
                          fontSize: '11px',
                          fontWeight: '600',
                        }}>
                          ROAD CLOSED
                        </span>
                      )}
                      {props.isMajor && !props.isClosure && (
                        <span style={{
                          backgroundColor: '#f97316',
                          color: 'white',
                          padding: '4px 12px',
                          borderRadius: '14px',
                          fontSize: '11px',
                          fontWeight: '600',
                        }}>
                          MAJOR
                        </span>
                      )}
                      <span style={{
                        backgroundColor: 'var(--bg-tertiary)',
                        color: 'var(--text-secondary)',
                        padding: '4px 12px',
                        borderRadius: '14px',
                        fontSize: '11px',
                        fontWeight: '500',
                        border: '1px solid var(--border-color)',
                      }}>
                        {props.category || props.hazardType.toUpperCase()}
                      </span>
                    </div>

                    {/* Affected roads - prominent display */}
                    {props.roads && props.roads.length > 0 && (
                      <div style={{
                        fontSize: '13px',
                        marginBottom: '12px',
                        padding: '10px 12px',
                        backgroundColor: 'var(--bg-tertiary)',
                        borderRadius: '6px',
                        borderLeft: `3px solid ${color}`,
                      }}>
                        <div style={{ fontWeight: '600', marginBottom: '4px', color: 'var(--text-primary)' }}>
                          Affected Roads
                        </div>
                        <div style={{ color: 'var(--text-secondary)', lineHeight: '1.5' }}>
                          {props.roads.slice(0, 4).map((road, i) => (
                            <div key={i}>{road}</div>
                          ))}
                          {props.roads.length > 4 && (
                            <div style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>
                              +{props.roads.length - 4} more roads
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Advice */}
                    {(props.adviceA || props.adviceB) && (
                      <div style={{ marginBottom: '12px' }}>
                        {props.adviceA && (
                          <div style={{
                            fontSize: '13px',
                            marginBottom: '6px',
                            lineHeight: '1.5',
                            color: 'var(--text-primary)',
                          }}>
                            {props.adviceA}
                          </div>
                        )}
                        {props.adviceB && (
                          <div style={{
                            fontSize: '12px',
                            lineHeight: '1.5',
                            color: 'var(--text-muted)',
                          }}>
                            {props.adviceB}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Timestamps */}
                    <div style={{
                      fontSize: '12px',
                      color: 'var(--text-muted)',
                      paddingTop: '10px',
                      borderTop: '1px solid var(--border-color)',
                      display: 'flex',
                      justifyContent: 'space-between',
                      flexWrap: 'wrap',
                      gap: '8px',
                    }}>
                      <div>
                        <span style={{ color: 'var(--text-muted)' }}>Updated:</span>{' '}
                        <span style={{ color: 'var(--text-secondary)' }}>{formatTimeAgo(props.lastUpdated)}</span>
                      </div>
                      {props.endTime && (
                        <div>
                          <span style={{ color: 'var(--text-muted)' }}>Expected end:</span>{' '}
                          <span style={{ color: 'var(--text-secondary)' }}>{formatDate(props.endTime)}</span>
                        </div>
                      )}
                      {props.nextUpdateDue && (
                        <div>
                          <span style={{ color: 'var(--text-muted)' }}>Next update expected:</span>{' '}
                          <span style={{ color: 'var(--text-secondary)' }}>{formatDate(props.nextUpdateDue)}</span>
                        </div>
                      )}
                    </div>

                    {/* Disclaimer */}
                    <div style={{
                      marginTop: '12px',
                      padding: '10px 12px',
                      backgroundColor: 'rgba(251, 191, 36, 0.15)',
                      border: '1px solid rgba(251, 191, 36, 0.3)',
                      borderRadius: '6px',
                      fontSize: '11px',
                      color: 'rgb(251, 191, 36)',
                      lineHeight: '1.5',
                    }}>
                      <strong>Note:</strong> Always check current road conditions before travelling.
                      <br />
                      <span style={{ fontSize: '10px', opacity: 0.8 }}>
                        Source: {props.state === 'VIC' ? 'VicRoads / Dept. of Transport' : 'Transport for NSW'}
                      </span>
                    </div>
                  </div>
                </div>
              </Popup>
            </Marker>
          </Fragment>
        );
      })}
    </>
  );
}
